import { BUDGETS, GIFTS, type Budget, type Gift } from "../data/presents";
import { randomFloat } from "./random";

export interface PresentInput {
  recipient: string;
  budget: Budget;
  vibes: string[];
  occasion?: string;
}

export interface ScoredGift extends Gift {
  score: number;
  budgetMatch: boolean;
}

const budgetIndex = (b: Budget) => BUDGETS.findIndex((x) => x.id === b);

export const fitsPresent = (gift: Gift, input: PresentInput) =>
  gift.recipients.includes(input.recipient) && (!input.vibes.length || gift.vibes.some((v) => input.vibes.includes(v)));

const OCCASION_RULES: Record<string, { vibes: string[]; words: RegExp; budgets?: Budget[] }> = {
  birthday: { vibes: ["funny", "fancy", "adventurous", "creative", "techy"], words: /experience|day|voucher|kit|game|personalised|ticket|class|session/i },
  christmas: { vibes: ["cosy", "foodie", "fancy"], words: /hamper|socks|blanket|throw|chocolate|candle|slipper|cocoa|jigsaw|board game|gown|scarf|mug|tin/i },
  "secret-santa": { vibes: ["funny", "foodie", "practical"], words: /mug|socks|desk|notebook|sweets|biscuit|chocolate|game|toy|tea|mini/i, budgets: ["under-20"] },
  anniversary: { vibes: ["fancy", "cosy", "adventurous"], words: /for two|couple|personalised|engraved|jewell|star map|photo|date|spa|romantic|dinner|break|night/i },
  "thank-you": { vibes: ["foodie", "cosy", "practical", "fancy"], words: /hamper|plant|tea|coffee|biscuit|chocolate|voucher|candle|planner|notebook|flowers|bag/i },
};

export const occasionFit = (gift: Gift, occasion?: string) => {
  const rule = occasion ? OCCASION_RULES[occasion] : undefined;
  if (!rule) return 0;
  if (occasion && gift.occasions?.includes(occasion)) return 4;
  const vibeHits = gift.vibes.filter((v) => rule.vibes.includes(v)).length;
  return Math.min(vibeHits, 2) + (rule.words.test(gift.name) ? 1.5 : 0) + (rule.budgets?.includes(gift.budget) ? 1 : 0);
};

export function scoreGift(gift: Gift, input: PresentInput, jitter = 0.9): ScoredGift {
  const gap = Math.abs(budgetIndex(gift.budget) - budgetIndex(input.budget));
  const primary = input.vibes.length && gift.vibes[0] === input.vibes[0] ? 0.5 : 0;
  return { ...gift, score: -gap * 10 + primary + occasionFit(gift, input.occasion) + randomFloat() * jitter, budgetMatch: gap === 0 };
}

const ranked = (input: PresentInput, jitter: number, gifts: Gift[]) => {
  const fits = gifts.filter((g) => fitsPresent(g, input));
  const pool = fits.length ? fits : gifts.filter((g) => g.recipients.includes(input.recipient));
  return pool.map((g) => scoreGift(g, input, jitter)).sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
};

export function rankPresents(input: PresentInput, count = 6, gifts: Gift[] = GIFTS): ScoredGift[] {
  return ranked(input, 0, gifts).slice(0, count);
}

const STOP = new Set(["a", "an", "the", "and", "of", "for", "set", "kit", "with", "&", "gift", "box", "mini", "personalised"]);
const words = (name: string) => new Set(name.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter((w) => w.length > 1 && !STOP.has(w)));

export const similarGifts = (a: string, b: string) => {
  const A = words(a), B = words(b);
  if (!A.size || !B.size) return a.toLowerCase() === b.toLowerCase();
  const shared = Array.from(A).filter((w) => B.has(w)).length;
  return shared / Math.min(A.size, B.size) >= 0.6;
};

// Strict match: recipient + exact budget + chosen vibe; occasion via tags, else best guess (tier 1 = guessed fit, tier 2 = untagged no-fit fill).
const occasionTier = (g: Gift, occasion?: string) => {
  if (!occasion) return 0;
  if (g.occasions?.length) return g.occasions.includes(occasion) ? 0 : -1;
  return occasionFit(g, occasion) > 0 ? 0 : 1;
};

export function findPresents(input: PresentInput, count = 5, gifts: Gift[] = GIFTS, exclude: string[] = []): ScoredGift[] {
  const pool = gifts
    .filter((g) => fitsPresent(g, input) && g.budget === input.budget && !exclude.some((n) => similarGifts(n, g.name)))
    .map((g) => ({ g: scoreGift(g, input, 0.9), tier: occasionTier(g, input.occasion) }))
    .filter((x) => x.tier >= 0)
    .sort((a, b) => a.tier - b.tier || Number(!!b.g.has_link) - Number(!!a.g.has_link) || b.g.score - a.g.score);
  const out: ScoredGift[] = [];
  for (const { g } of pool) {
    if (out.length >= count) break;
    if (!out.some((o) => similarGifts(o.name, g.name))) out.push(g);
  }
  return out;
}
